import React, { useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useAuth } from '../../lib/auth';
import { LanguageSwitcher } from '../settings/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export function Navigation() {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    if (!user) return null;

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    const linkClass = (path: string) => {
        return `px-4 py-2 rounded-lg transition-colors ${
            isActive(path)
                ? 'bg-blue-50 text-blue-600 font-medium'
                : 'text-gray-600 hover:bg-gray-50'
        }`;
    };

    const mobileLinkClass = (path: string) => {
        return `block px-3 py-2 rounded-md text-base font-medium ${
            isActive(path)
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
        }`;
    };

    return (
        <nav className="bg-white border-b border-gray-200">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative flex h-16 items-center justify-between">
                    {/* Mobile menu button */}
                    <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                        >
                            <span className="sr-only">Open main menu</span>
                            {/* Hamburger icon */}
                            {!mobileMenuOpen ? (
                                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                                </svg>
                            ) : (
                                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Logo and desktop navigation */}
                    <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                        <div className="flex shrink-0 items-center">
                            <Link to="/" className="text-xl font-bold text-gray-900">
                                {t('common:app_name')}
                            </Link>
                        </div>
                        {/* Desktop navigation links */}
                        <div className="hidden sm:ml-6 sm:block">
                            <div className="flex gap-2">
                                <Link to="/" className={linkClass('/')}>
                                    {t('common:dashboard')}
                                </Link>
                                <Link to="/accounts" className={linkClass('/accounts')}>
                                    {t('accounts:title')}
                                </Link>
                                <Link to="/transactions" className={linkClass('/transactions')}>
                                    {t('transactions:title')}
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Right side items */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                        <div className="hidden sm:flex items-center gap-4">
                            <LanguageSwitcher />
                            <span className="text-sm text-gray-600">
                                {t('common:welcome')}, <span className="font-medium">{user.username}</span>
                            </span>
                            <button
                                onClick={logout}
                                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                {t('common:logout')}
                            </button>
                        </div>
                        {/* Mobile user icon */}
                        <div className="sm:hidden flex items-center">
                            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
                                {user.username.charAt(0).toUpperCase()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
                <div className="sm:hidden border-t border-gray-200">
                    <div className="space-y-1 px-2 pb-3 pt-2">
                        <Link to="/" className={mobileLinkClass('/')} onClick={() => setMobileMenuOpen(false)}>
                            {t('common:dashboard')}
                        </Link>
                        <Link to="/accounts" className={mobileLinkClass('/accounts')} onClick={() => setMobileMenuOpen(false)}>
                            {t('accounts:title')}
                        </Link>
                        <Link to="/transactions" className={mobileLinkClass('/transactions')} onClick={() => setMobileMenuOpen(false)}>
                            {t('transactions:title')}
                        </Link>
                        <div className="border-t border-gray-200 my-2 pt-2">
                            <div className="px-3 py-2">
                                <LanguageSwitcher />
                            </div>
                            <div className="px-3 py-2 text-sm text-gray-600">
                                {t('common:welcome')}, <span className="font-medium">{user.username}</span>
                            </div>
                            <button
                                onClick={() => {
                                    logout();
                                    setMobileMenuOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
                            >
                                {t('common:logout')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}
